<template>
  <li :class="{
      'af-expansion-panel--level-2': isLevelTwo,
      'af-expansion-panel--level-3': isLevelThree,
      'af-expansion-panel--active': expanded,
      'af-expansion-panel--is-folder': isFolder
    }" class="af-expansion-panel">
    <div
      :class="{
        'panel--level-2': isLevelTwo,
        'panel--level-3': isLevelThree,
        'panel--is-folder': isFolder,
        'panel--active': expanded
      }"
      class="panel">

      <div
        :class="'panel__checkbox-indicator ' + status"
        role="button"
        @click="addToQuery">
      </div>

      <div class="panel__title" @click="toggleExpanded">
        <div class="panel__title-name">
          {{ name }}
          <strong v-if="numberOfSelectedChildren">{{ numberOfSelectedChildren }} val</strong>
        </div>

        <div class="panel__toggle" v-if="isFolder">
          <span
            v-if="expanded"
            class="panel__close">
            Stäng
            <icon :fill="isLevelOne ? 'white' : 'black'" type="angle-up"></icon>
          </span>

          <icon
            v-else
            class="panel__icon"
            link
            type="angle-down">
          </icon>
        </div>
      </div>
    </div>

    <div
      v-if="isExperiences"
      class="panel__range">
      <af-range
        @newValueSet="addYearsToQuery($event, model)"
        :config="{step: 1, connect: true, range: {min: [0], max: [10]}}"
        :values="[rangeMin, rangeMax]">
        Vilken erfarenhet efterfrågas?
      </af-range>
    </div>

    <div 
      v-if="isSkills"
      class="panel__range">
      <div class="panel__range-dropdown">
        <af-h4 class="panel__range-title">Vilken erfarenhet efterfrågas?</af-h4>
        <search-dropdown
          :options="skillYears"
          :value="rangeMin"
          inlinePlaceholder="Välj kompetensnivå"
          label="Vilken erfarenhet efterfrågas?"
          name="educationYears"
          @changed="addYearsToQuery">
        </search-dropdown>
      </div>
    </div>

    <ul v-show="expanded" v-if="isFolder">
      <af-expansion-panel
        v-for="child in children"
        :key="child.path"
        :level="level + 1"
        :model="child"
        :queryType="queryType"
        @openParent="expandPanel">
      </af-expansion-panel>
    </ul>
  </li>
</template>

<script>
import Icon from '@/components/Icon'
import AfCheckbox from '@/components/AfCheckbox'
import AfRange from '@/components/AfRange'
import AfH4 from '@/components/AfH4'
import SearchDropdown from '@/views/Search/SearchDropdown'
import * as types from '@/store/actionTypes'

export default {
  name: 'AfExpansionPanel',
  components: {
    AfCheckbox,
    AfRange,
    Icon,
    SearchDropdown,
    AfH4
  },

  props: {
    level: {
      default: 1,
      type: Number
    },
    model: Object,
    queryType: String
  },

  data() {
    return {
      // New stuff
      parent: this.model.parent || 'container',
      name: this.model.name,
      path: this.model.path,
      children: [],
      status: 'unchecked',
      years: 0,

      rangeMin: 0,
      rangeMax: 10,

      // Old stuff
      expanded: false,
      skillYears: [
        {
          name: 'Minst någon erfarenhet',
          path: 0
        },
        {
          name: 'Minst 1 års erfarenhet',
          path: 1
        },
        {
          name: 'Minst 2 års erfarenhet',
          path: 2
        },
        {
          name: 'Minst 5 års erfarenhet',
          path: 5
        }
      ],
      modifier: 'gte'
    }
  },

  created() {
    const model = this.model

    if (model.children) {
      this.children = model.children.map(child => {
        return {
          ...child,
          status: 'unchecked',
          parent: model.path
        }
      })
    }
  },

  mounted() {
    // Reset values when pressing the reset query button
    window.eventBus.$on('RESET_QUERY', this.resetQuery)

    // Handle changes in categories above the clicked category
    window.eventBus.$on('upside', this.handleUpside)

    // Handle changes in categories below the clicked category
    window.eventBus.$on('downside', this.handleDownside)

    // Handle values from local and global search
    window.eventBus.$on('openExpansionPanel', this.handleLocalAndGlobalSearch)

    // Handle closing all expanded elements when switching tabs
    window.eventBus.$on('close-all-boxes', this.closePanel)

    // Handle removing a tag - SearchTag
    window.eventBus.$on('removeTag', this.handleRemoveTag)

    // Handle local clear choices - SearchClear
    window.eventBus.$on('clearLocal', this.handleClearLocal)

    // Handle recommended search item - SearchResultsRecommended
    window.eventBus.$on('searchRecommended', this.handleSearchRecommended)
  },

  beforeDestroy() {
    // Kill all listeners
    window.eventBus.$off()
  },

  computed: {
    allChildrenChecked() {
      return this.children.every(({ status }) => status === 'checked')
    },
    allChildrenUnchecked() {
      return this.children.every(({ status }) => status === 'unchecked')
    },
    isFolder() {
      return this.children && this.children.length > 0
    },
    isLevelOne() {
      return this.level === 1
    },
    isLevelTwo() {
      return this.level === 2
    },
    isLevelThree() {
      return this.level === 3
    },
    isExperiences() {
      return this.status === 'checked' && this.queryType === 'experiences'
    },
    isSkills() {
      return this.status === 'checked' && this.queryType === 'skills'
    },
    numberOfSelectedChildren() {
      if (!this.children.length) {
        return 0
      }

      return this.children.filter(({ status }) => status === 'checked').length
    }
  },

  methods: {
    handleRemoveTag({ path, type }) {
      if (
        this.path.replace('county-', '') === path &&
        this.queryType === type &&
        this.status === 'checked'
      ) {
        this.resetQuery()
        this.emitUpsideAndDownsideEvent(this.path, 'unchecked', this.queryType)
      }
    },

    handleClearLocal(type) {
      if (this.queryType === type) {
        this.resetQuery()
      }
    },

    handleSearchRecommended({ path, type }) {
      if (
        this.path.replace('county-', '') === path &&
        this.queryType === type
      ) {
        this.setStatus('checked')
        this.emitUpsideAndDownsideEvent(this.path, 'checked', this.queryType)
      }
    },

    toggleExpanded() {
      if (this.isFolder) {
        this.expanded = !this.expanded
      }
    },

    expandPanel() {
      if (this.isFolder) {
        this.expanded = true
      }
    },

    closePanel() {
      if (this.isFolder) {
        this.expanded = false
      }
    },

    resetQuery() {
      this.status = 'unchecked'
      this.setChildStatuses('unchecked')
      this.expanded = false
      this.years = 0
    },

    setStatus(status) {
      this.status = status
    },

    setChildStatuses(status) {
      this.children = this.children.map(child => ({
        ...child,
        status
      }))
    },

    emitUpsideAndDownsideEvent(from, status, type) {
      this.emitUpsideEvent(from, status, type)
      this.emitDownsideEvent(from, status, type)
    },

    emitUpsideEvent(from, status, type) {
      window.eventBus.$emit('upside', {
        from,
        status,
        type
      })
    },

    emitDownsideEvent(from, status, type) {
      window.eventBus.$emit('downside', {
        from,
        status,
        type
      })
    },

    handleLocalAndGlobalSearch({ name, path, years, type }) {
      if (
        this.path.replace('county-', '') === path &&
        this.queryType === type
      ) {
        this.rangeMin = years && years[0] ? years[0].years : 0
        this.rangeMax = years && years[1] ? years[1].years : 10

        this.status = 'checked'
        this.setChildStatuses('checked')
        this.commitToQuery(name, path)
        this.emitUpsideAndDownsideEvent(this.path, 'checked', this.queryType)
      }
    },

    removeFromQuery(path, type) {
      this.$store.commit(types.REMOVE_FROM_QUERY, {
        path,
        type
      })
    },

    handleUpside(payload) {
      const targetArr = this.children.filter(
        child => child.path === payload.from
      )

      if (targetArr.length === 1 && payload.type === this.queryType) {
        const target = targetArr[0]

        target.status = payload.status

        if (this.allChildrenChecked) {
          this.setStatus('checked')
          this.commitClear()
          this.commitToQuery(this.name, this.path)
        } else if (this.allChildrenUnchecked) {
          this.setStatus('unchecked')
        } else {
          this.setStatus('indeterminate')

          this.children.forEach(child => {
            const alreadyAdded = this.$store.getters.hasValue(
              child.path,
              this.queryType
            )

            if (child.status === 'checked' && !alreadyAdded) {
              this.commitToQuery(child.name, child.path)
            } else if (child.status !== 'checked' && alreadyAdded) {
              this.removeFromQuery(child.path, this.queryType)
            }
          })
        }

        this.emitUpsideEvent(this.path, this.status, this.queryType)
      }
    },

    handleDownside(payload) {
      if (payload.from === this.parent && payload.type === this.queryType) {
        if (payload.status === 'checked') {
          this.setStatus('checked')
          this.setChildStatuses('checked')
        } else if (payload.status === 'unchecked') {
          this.setStatus('unchecked')
          this.setChildStatuses('unchecked')
        }

        this.emitDownsideEvent(this.path, this.status, this.queryType)
      }
    },

    commitToQuery(name, path) {
      this.$store.commit(types.ADD_TO_QUERY, {
        payload: {
          name,
          path
        },
        type: this.queryType
      })
    },

    commitClear() {
      this.$store.commit(types.CLEAR_QUERY_OF_TYPE, {
        path: this.path,
        type: this.queryType
      })
    },

    addToQuery() {
      if (this.status === 'checked') {
        this.setStatus('unchecked')
        this.setChildStatuses('unchecked')
        this.commitClear()

        if (this.isLevelTwo || this.isLevelThree) {
          this.removeFromQuery(this.parent, this.queryType)
        }
      } else {
        this.setStatus('checked')
        this.setChildStatuses('checked')

        if (this.isLevelOne || this.isLevelTwo) {
          this.commitClear()
          this.commitToQuery(this.name, this.path)
        }
      }

      this.emitUpsideAndDownsideEvent(this.path, this.status, this.queryType)
    },

    addYearsToQuery(payload) {
      if (this.queryType === 'experiences') {
        const gt = parseInt(payload[0], 10)
        const lt = parseInt(payload[1], 10)

        let years = [
          {
            modifier: 'gte',
            years: gt
          }
        ]

        if (lt !== 10) {
          years = years.concat([
            {
              modifier: 'lte',
              years: lt
            }
          ])
        }

        this.$store.commit(types.ADD_EXPERIENCE_YEARS, {
          years,
          name: this.name,
          path: this.path,
          type: this.queryType
        })
      } else {
        this.rangeMin = payload.path

        this.$store.commit(types.ADD_SKILL_YEARS, {
          years: [
            {
              modifier: this.modifier,
              years: payload.path
            }
          ],
          name: this.name,
          path: this.path,
          type: this.queryType
        })
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
@import 'variables/custom-media.css';

.panel__range-title {
  text-align: center;
  margin-bottom: var(--spacing-half) !important;
}

.af-expansion-panel {
  border-bottom: 1px solid var(--color-silver-chalice);
}

.af-expansion-panel--active.af-expansion-panel--is-folder {
  border-bottom: 0;
}

.af-expansion-panel--level-2 {
  background-color: var(--color-porcelain);
  border-color: var(--color-silver-sand);
  color: var(--color-black);
}

.panel {
  align-items: center;
  color: var(--color-black);
  display: flex;
  padding: var(--spacing-third) var(--spacing-half);
  user-select: none;

  &:hover {
    background-color: var(--color-porcelain);
  }

  @media (--bp-desktop) {
    padding: var(--spacing-third);
    padding-right: var(--spacing-half);
  }
}

.panel.panel--is-folder {
  cursor: pointer;
}

.panel__title {
  align-items: center;
  display: flex;
  flex: 1;
  font-size: 16px;
}

.panel__title-name {
  max-width: 80%;

  &:first-letter {
    text-transform: uppercase;
  }
}

.panel--active.panel--is-folder,
.panel--active.panel--is-folder:hover {
  background-color: var(--color-matisse);
  color: var(--color-white);
}

.panel__toggle {
  margin-left: auto;
}

.panel__icon {
  font-size: 20px;
}

.panel__close {
  align-items: center;
  color: var(--color-white);
  display: flex;
  font-size: 12px;
  text-transform: uppercase;
}

.panel__close-icon {
  margin-left: 5px;
}

.panel--level-2 {
  padding: var(--spacing-half) var(--spacing-half) var(--spacing-half)
    var(--spacing);
}

.panel--level-2.panel--active.panel--is-folder,
.panel--level-2.panel--active.panel--is-folder:hover {
  background-color: var(--color-porcelain);
  color: var(--color-black);
}

.panel--level-2.panel--active.panel--is-folder .panel__close,
.panel--level-2.panel--active.panel--is-folder .panel__close > svg {
  color: var(--color-black);
  fill: var(--color-black);
}

.panel--level-3 {
  background-color: var(--color-iron);
  border-bottom: 1px solid var(--color-submarine);
  color: var(--color-black);
  padding-left: var(--spacing-huge);
}

.panel--level-3.panel--active.panel--is-folder,
.panel--level-3.panel--active.panel--is-folder:hover {
  background-color: var(--color-porcelain);
  border-bottom: none;
  color: var(--color-black);
}

.panel--level-3.panel--active.panel--is-folder .panel__close {
  color: var(--color-black);
}

.panel__range {
  padding: var(--spacing-medium);
}

.af-expansion-panel.af-expansion-panel--active .panel__range {
  background-color: var(--color-porcelain);
}

.af-expansion-panel.af-expansion-panel--level-2.af-expansion-panel--active
  .panel__range {
  background-color: var(--color-porcelain);
}

.af-expansion-panel.af-expansion-panel--level-3 .panel__range {
  background-color: var(--color-submarine-dark) !important;
}

.panel__range-dropdown {
  background-color: var(--color-iron);
  border-radius: 4px;
  padding: var(--spacing-medium);
}

.panel__checkbox {
  display: none;
}

.panel__checkbox-indicator {
  background-color: var(--color-white);
  border: 1px solid var(--color-mine-shaft);
  border-radius: 3px;
  color: var(--color-black);
  cursor: pointer;
  display: block;
  height: 16px;
  margin-right: var(--spacing-third);
  position: relative;
  width: 16px;
  z-index: 1;
}

.panel__checkbox-indicator.indeterminate:before {
  content: '\f068';
  font-family: 'FontAwesome';
  font-size: 12px;
  left: 50%;
  position: absolute;
  top: calc(50% + 1px);
  transform: translateX(-50%) translateY(-50%);

  @media (--ie11) {
    content: url('./../../static/icons/check.svg');
    height: 12px;
    width: 12px;
  }
}

.panel__checkbox-indicator.checked:before {
  content: '\f00c';
  font-family: 'FontAwesome';
  font-size: 12px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);

  @media (--ie11) {
    content: url('./../../static/icons/check.svg');
    height: 12px;
    top: 20%;
    width: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AfExpansionPanel.vue